import { CompareAndSwapResult, StorageApi } from './StorageApi'
import {
  validateStorageKey,
  validateStorageValue,
  validateStorageBatchSize,
  validateStorageMultiGetSize,
} from './validateStorage'

/**
 * Wraps a StorageApi (MockStorageApi / FirestoreStorageApi) so the
 * non-production storage backends reject exactly what the server rejects,
 * matching the RundotApiError a real device surfaces. Applied only to
 * server-backed scopes (appStorage / ownerStorage / sharedStorage). deviceCache
 * is device-local and unvalidated in prod, so it is never wrapped.
 */
export class ValidatingStorageApi implements StorageApi {
  constructor(private readonly delegate: StorageApi) {}

  key(index: number): Promise<string | null> {
    return this.delegate.key(index)
  }
  clear(): Promise<void> {
    return this.delegate.clear()
  }
  length(): Promise<number> {
    return this.delegate.length()
  }
  getAllItems(): Promise<string[]> {
    return this.delegate.getAllItems()
  }
  getAllData(): Promise<Record<string, string>> {
    return this.delegate.getAllData()
  }
  getItem(key: string): Promise<string | null> {
    return this.delegate.getItem(key)
  }

  // The mutating methods are async so a synchronous validation throw surfaces as
  // a rejected promise — matching the production transport, which always rejects
  // (never throws synchronously) on a server validation failure.
  async setItem(key: string, item: string): Promise<void> {
    validateStorageKey(key)
    validateStorageValue(item)
    return this.delegate.setItem(key, item)
  }

  async removeItem(key: string): Promise<void> {
    validateStorageKey(key)
    return this.delegate.removeItem(key)
  }

  async setMultipleItems(items: { key: string; value: string }[]): Promise<void> {
    validateStorageBatchSize(items.length)
    for (const { key, value } of items) {
      validateStorageKey(key)
      validateStorageValue(value)
    }
    return this.delegate.setMultipleItems(items)
  }

  async removeMultipleItems(keys: string[]): Promise<void> {
    validateStorageBatchSize(keys.length)
    for (const key of keys) validateStorageKey(key)
    return this.delegate.removeMultipleItems(keys)
  }

  async compareAndSwap(
    key: string,
    expectedValue: string | null,
    nextValue: string | null,
  ): Promise<CompareAndSwapResult> {
    validateStorageKey(key)
    if (expectedValue !== null) validateStorageValue(expectedValue)
    if (nextValue !== null) validateStorageValue(nextValue)
    return this.delegate.compareAndSwap(key, expectedValue, nextValue)
  }

  async getMultipleItems(keys: string[]): Promise<Record<string, string>> {
    validateStorageMultiGetSize(keys.length)
    for (const key of keys) validateStorageKey(key)
    return this.delegate.getMultipleItems(keys)
  }
}
